import Keycloak from 'keycloak-js';
import keycloakConfig from './keycloakConfig.tsx';
import SecureTokenStorage from '../services/SecureTokenStorage';

let tokenRefreshInterval: NodeJS.Timeout | null = null;

const minTokenValidityInSeconds = 60; // 1 minute buffer

const keycloakInstance = new Keycloak({
  url: keycloakConfig.url,
  realm: keycloakConfig.realm,
  clientId: keycloakConfig.clientId,
});

const initOptions = {
  onLoad: keycloakConfig.onLoad,
  silentCheckSsoRedirectUri: window.location.origin + '/silent-check-sso.html',
};


const authCallbacks: Array<(token: string) => void> = [];

const addAuthCallback = (callback: (token: string) => void) => {
  authCallbacks.push(callback);
};

const handleCallback = (token: string, id: string) => {
  // Store tokens securely using cookies instead of localStorage
  SecureTokenStorage.setDocumentId(id);
  SecureTokenStorage.setAcrToken(token);
};

const scheduleTokenRefresh = () => {
  if (tokenRefreshInterval) {
    clearInterval(tokenRefreshInterval);
  }

  // Check every 30 seconds
  tokenRefreshInterval = setInterval(async () => {
    try {
      const refreshed = await keycloakInstance.updateToken(minTokenValidityInSeconds);
      if (refreshed && keycloakInstance.token) {
        // Store refreshed token securely
        SecureTokenStorage.setAccessToken(keycloakInstance.token);
        console.info('Token actualizado automáticamente y almacenado de forma segura.');
      }
    } catch (error) {
      console.error('Error al refrescar el token automáticamente:', error);
      
      // Clear tokens on failure and redirect to login
      SecureTokenStorage.clearAllTokens();
      
      // Clear the refresh interval to prevent further attempts
      if (tokenRefreshInterval) {
        clearInterval(tokenRefreshInterval);
        tokenRefreshInterval = null;
      }
      
      // Do not force login on background refresh failure to avoid page reloading loops
      // doLogin();
    }
  }, 30000);
};

const handleAuthSuccess = () => {
  scheduleTokenRefresh();
  
  // Store the access token securely
  if (keycloakInstance.token) {
    SecureTokenStorage.setAccessToken(keycloakInstance.token);
  }
  
  // Migrate existing localStorage data to secure cookies
  SecureTokenStorage.migrateFromLocalStorage();
  
  authCallbacks.forEach(callback => callback(keycloakInstance.token || ""));
  authCallbacks.length = 0;
};

const loginWithAcrToken = (acrValue: string, id: string,authority: string, redirectUri: string) => {
  // Use secure cookie storage instead of localStorage
  SecureTokenStorage.setCurrentAuthority(authority);
  SecureTokenStorage.setUserId(UserUuid() || '');


  const initOptionsAcr = {
    acrValues: acrValue,
    redirectUri,
  };
  keycloakInstance.login(initOptionsAcr)
    .then(() => {
      keycloakInstance.updateToken(5).then(() => {
        const token = keycloakInstance.token || "";
        if (!token) {
          console.error("Token no generado en loginWithAcrToken");
          return;
        }
        console.info(token);
        SecureTokenStorage.setAcrToken(token);
      }).catch(error => {
        console.error("Error al actualizar el token: ", error);
      });
    })
    .catch(error => {
      console.error(`Keycloak login exception: ${error}`);
    });
};

const Login = (onAuthenticatedCallback: Function) => {
  keycloakInstance.init(initOptions)
    .then(authenticated => {
      if (authenticated) {
        // // console.log("Authenticated");
        handleAuthSuccess();
        onAuthenticatedCallback();
      } else {
        console.error("User is not authenticated.");
        keycloakInstance.login();
      }
    })
    .catch(error => {
      console.error(`Keycloak init exception: ${error}`);
    });
};


const loginWithAcr = (acrValue: string, onAuthenticatedCallback: Function) => {
  keycloakInstance.login({ acrValues: acrValue })
    .then(onAuthenticatedCallback)
    .catch(error => {
      console.error('Authentication with ACR failed:', error);
    });
};



const UserName = () => keycloakInstance.tokenParsed?.preferred_username;
const UserUuid = () => keycloakInstance.idTokenParsed?.sub;
const UserRoles = () => keycloakInstance.tokenParsed?.Perfil || [];
const UserPermissions = () => keycloakInstance.tokenParsed?.Permisos || [];
const UserEmail = () => keycloakInstance.tokenParsed?.email || keycloakInstance.idTokenParsed?.email;

const RefreshToken = async (): Promise<boolean> => {
  try {
    const refreshed = await keycloakInstance.updateToken(minTokenValidityInSeconds);
    
    if (refreshed && keycloakInstance.token) {
      // Store the new token securely
      SecureTokenStorage.setAccessToken(keycloakInstance.token);
      // console.log('Token refreshed and stored securely');
      return true;
    }
    
    // Token was still valid, no refresh needed
    return refreshed !== undefined;
  } catch (err) {
    console.error('Failed to refresh token:', err);
    
    // Clear tokens on refresh failure
    SecureTokenStorage.clearAllTokens();
    return false;
  }
};

const Logout = () => {
  if (tokenRefreshInterval) {
    clearInterval(tokenRefreshInterval);
    tokenRefreshInterval = null;
  }
  
  // Clear all secure tokens
  SecureTokenStorage.clearAllTokens();
  
  keycloakInstance.logout();
};

const isLoggedIn = () => !!keycloakInstance.token;

const getToken = () => keycloakInstance.token;

const doLogin = () => keycloakInstance.login();

const updateToken = (successCallback: any) => {
  keycloakInstance.updateToken(minTokenValidityInSeconds)
    .then(successCallback)
    .catch(doLogin);
};

const refreshToken = () => {
  keycloakInstance.updateToken(minTokenValidityInSeconds);
};

// Enhanced token refresh with retry logic and proper error handling
const RefreshTokenWithRetry = async (maxRetries = 3): Promise<boolean> => {
  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      // console.log(`Token refresh attempt ${attempt}/${maxRetries}`);
      
      // Force refresh (-1) because if we are here, the current token likely failed
      const refreshed = await keycloakInstance.updateToken(-1);
      
      if (refreshed && keycloakInstance.token) {
        // Store the new token securely
        SecureTokenStorage.setAccessToken(keycloakInstance.token);
        // console.log(`Token refreshed successfully on attempt ${attempt}`);
        return true;
      }
      
      // With -1, refreshed should be true if successful. 
      // If false, it means error usually, but keycloak-js might behave differently.
      // However, usually -1 forces network call.
      
    } catch (error) {
      console.error(`Token refresh attempt ${attempt} failed:`, error);
      
      if (attempt === maxRetries) {
        // Final attempt failed, clear tokens and logout
        console.error('All token refresh attempts failed, logging out');
        SecureTokenStorage.clearAllTokens();
        doLogin();
        return false;
      }
      
      // Wait before retry (exponential backoff)
      const waitTime = Math.pow(2, attempt - 1) * 1000; // 1s, 2s, 4s
      // console.log(`Waiting ${waitTime}ms before retry...`);
      await new Promise(resolve => setTimeout(resolve, waitTime));
    }
  }
  
  return false;
};

// Check if token needs refresh (expires in less than 5 minutes)
const shouldRefreshToken = (): boolean => {
  if (!keycloakInstance.token || !keycloakInstance.tokenParsed?.exp) {
    return false;
  }
  
  const now = Date.now() / 1000;
  const tokenExp = keycloakInstance.tokenParsed.exp;
  const timeUntilExpiry = tokenExp - now;
  
  // Refresh if token expires in less than 5 minutes (300 seconds)
  return timeUntilExpiry < 300;
};

// Additional secure token methods
const getSecureAcrToken = () => SecureTokenStorage.getAcrToken();
const getSecureDocumentId = () => SecureTokenStorage.getDocumentId();
const getSecureUserId = () => SecureTokenStorage.getUserId();
const getSecureCurrentAuthority = () => SecureTokenStorage.getCurrentAuthority();
const getSecureSignatureSvg = () => SecureTokenStorage.getSignatureSvg();

const KeyCloakService = {
  CallLogin: Login,
  GetUserName: UserName,
  GetUserEmail: () => keycloakInstance.tokenParsed?.email || keycloakInstance.idTokenParsed?.email,
  GetUserRoles: UserRoles,
  GetUserUuid: UserUuid,
  GetUserPermissions: UserPermissions,
  CallLogout: Logout,
  IsLoggedIn: isLoggedIn,
  GetToken: getToken,
  UpdateToken: updateToken,
  LoginWithAcr: loginWithAcr,
  LoginWithAcrToken: loginWithAcrToken,
  authServerUrl: keycloakConfig.url,
  realm: keycloakConfig.realm,
  HandleCallBack: handleCallback,
  RefreshToken: refreshToken,
  // Secure token storage methods
  GetSecureAcrToken: getSecureAcrToken,
  GetSecureDocumentId: getSecureDocumentId,
  GetSecureUserId: getSecureUserId,
  GetSecureCurrentAuthority: getSecureCurrentAuthority,
  GetSecureSignatureSvg: getSecureSignatureSvg,
  // Enhanced token refresh methods
  RefreshTokenWithRetry: RefreshTokenWithRetry,
  ShouldRefreshToken: shouldRefreshToken
};

export default KeyCloakService;
